import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";

import Button from "components/form/Button";
import InputField from "components/form/InputField";
import CustomModal from "components/ui-component/CustomModal";
import {
  ChatIcon,
  DisclosureCloseIcon,
  DisclosureOpenIcon,
  EmailIcon,
  MessageChat,
  SuccessIcon,
} from "utils/icon";
import { supportPost } from "store/slices/supportSlice";
import { previewDataGet } from "store/slices/paymentsSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { leadData, useLeadData } from "store/slices/dashboardSlice";
import { useIsLogin } from "store/slices/commonSlice";
import { decode, formatNumber } from "utils/HelperFunctions";
import { RootState } from "store/store";
import { useTestAttrs, useTid } from "utils/testid";

type SupportFormState = {
  name: string;
  email: string;
  message: string;
};

type SupportErrorState = {
  name: string;
  email: string;
  message: string;
};

type SupportOptions = {
  id: number;
  Icon: React.ElementType;
  title: string;
  description: React.ReactNode;
  onClick: () => void;
  className: string;
  testId: string;
};

type FAQQuestion = {
  id: number;
  question: string;
  answer: string | JSX.Element;
};

const SupportCard: React.FC<{
  Icon: React.ElementType;
  title: string;
  description: React.ReactNode;
  onClick: () => void;
  className?: string;
  testId: string;
}> = ({ Icon, title, description, onClick, className = "", testId }) => (
  <div
    className={`rounded-[12px] bg-bgSecondary lg:h-[250px] h-[230px] grid place-content-center p-10 w-full ${className}`}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    role="button"
    tabIndex={0}
    data-testid={testId}
    aria-label={title}
  >
    <div className="flex flex-col items-center gap-3">
      <Icon aria-hidden="true" />
      <p className="text-txtPrimary text-xl font-bold">{title}</p>
      {/* "anywhere", not "break-words": the card centres its content with a grid,
          which sizes the column to the content's min-content width. Only
          "anywhere" lowers that width, so a long email address wraps inside
          the padding instead of widening the column past the card. */}
      <p className="text-lg text-txtParagraph [overflow-wrap:anywhere] w-full">
        {description}
      </p>
    </div>
  </div>
);

const FAQItem: React.FC<{
  id: number;
  question: string;
  answer: string | JSX.Element;
  isLoading?: boolean;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ id, question, answer, isLoading = false, isOpen, onToggle }) => {
  const tid = useTid();

  return (
    <div className="py-6 border-b w-full" data-testid={tid(`faq-${id}`)}>
      <button
        type="button"
        className="group flex w-full items-center justify-between"
        data-testid={tid(`faq-${id}-toggle`)}
        aria-label={`${isOpen ? "Collapse" : "Expand"} FAQ: ${question}`}
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span
          className={`lg:text-xl sm:text-lg text-base font-medium text-start w-full ${isOpen ? "text-txtPrimary " : "text-txtSecondary"}`}
        >
          {question}
        </span>
        {isOpen ? (
          <DisclosureOpenIcon aria-hidden="true" />
        ) : (
          <DisclosureCloseIcon aria-hidden="true" />
        )}
      </button>
      {isOpen && (
        <div
          className="mt-2 lg:text-base sm:text-sm text-xs text-start w-full text-txtParagraph pr-7"
          data-testid={tid(`faq-${id}-answer`)}
        >
          {isLoading ? <Skeleton count={2} /> : answer}
        </div>
      )}
    </div>
  );
};

const SupportPage: React.FC = () => {
  const tid = useTid();
  const sectionAttrs = useTestAttrs("main", "Support and help center content");
  const mobileCardsAttrs = useTestAttrs(
    "contact-options-mobile",
    "Support contact options"
  );
  const desktopCardsAttrs = useTestAttrs(
    "contact-options-desktop",
    "Support contact options"
  );
  const faqAttrs = useTestAttrs("faq-section", "Frequently asked questions");
  const requestFormAttrs = useTestAttrs("request-form", "Support request form");
  const successAttrs = useTestAttrs(
    "request-success",
    "Support request success message"
  );
  const [isOpenRequest, setIsOpenRequest] = useState<boolean>(false);
  // Single-expand accordion: holds the id of the only open FAQ, if any.
  const [openFaqId, setOpenFaqId] = useState<number | null>(1);
  const [formState, setFormState] = useState<SupportFormState>({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState<SupportErrorState>({
    name: "",
    email: "",
    message: "",
  });
  const [successMessage, setSuccessMessage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const accountLeadData = useLeadData();
  const { companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );
  const isLogin = useIsLogin();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const hasCompanyDetails =
    !!companyDetails && Object.keys(companyDetails).length > 0;
  const [companyFetchSettled, setCompanyFetchSettled] =
    useState<boolean>(false);

  useEffect(() => {
    if (hasCompanyDetails || !clusterId) {
      setCompanyFetchSettled(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await dispatch(previewDataGet({ cluster_id: clusterId }));
      } catch (error) {
        console.log("support company details fetch error =====", error);
      } finally {
        if (!cancelled) setCompanyFetchSettled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Skeletons only until the fetch settles; if it fails, the static
  // fallback contact details below take over instead of loading forever.
  const isCompanyLoading = !hasCompanyDetails && !companyFetchSettled;

  const supportOptions = useMemo<SupportOptions[]>(
    () => [
      {
        id: 1,
        Icon: EmailIcon,
        title: "Email Us",
        description: isCompanyLoading ? (
          <Skeleton width={210} />
        ) : (
          companyDetails?.email || "examplecustomer@support.com"
        ),
        onClick: () => {
          if (isCompanyLoading) return;
          window.location.href = `mailto:${companyDetails?.email || "examplecustomer@support.com"}`;
        },
        className: "cursor-pointer",
        testId: "contact-option-email",
      },
      {
        id: 2,
        Icon: ChatIcon,
        title: "Call Us",
        description: isCompanyLoading ? (
          <Skeleton width={150} />
        ) : (
          formatNumber(
            companyDetails?.phone_number,
            companyDetails?.countryCode || "1",
            true
          ) || "+1 (404) 410 4100"
        ),
        onClick: () => {
          if (isCompanyLoading) return;
          // Same number the card shows: country code + digits, or the fallback.
          const phoneDigits = companyDetails?.phone_number
            ? `${companyDetails?.countryCode || "1"}${companyDetails.phone_number}`
            : "14044104100";
          window.location.href = `tel:+${phoneDigits.replace(/\D/g, "")}`;
        },
        className: "cursor-pointer",
        testId: "contact-option-call",
      },
      {
        id: 3,
        Icon: MessageChat,
        title: "Submit a Request",
        description: "We’ll get back to you soon",
        onClick: () => setIsOpenRequest(true),
        className: "cursor-pointer",
        testId: "contact-option-request",
      },
    ],
    [companyDetails, isCompanyLoading]
  );

  const faqQuestions: FAQQuestion[] = useMemo(
    () => [
      {
        id: 1,
        question:
          "I am asked to enter my account ID number on the login page. What is my account ID number?",
        answer: `Your account ID number is the unique identifier that ${companyDetails?.name || companyDetails?.company_name} uses for your personal profile. If you're not sure of your Account ID number, it's best to reach out to a ${companyDetails?.name || companyDetails?.company_name} representative for assistance.`,
      },
      {
        id: 2,
        question:
          "Is my financial information secure when using the customer portal?",
        answer:
          "Absolutely, your financial information is securely encrypted and is handled following stringent compliance standards and security protocols to ensure its safety.",
      },
      {
        id: 3,
        question: "How do I view my payment history?",
        answer: (
          <>
            You can access your payment history through several routes:
            <br />
            <br />
            1. From the Dashboard, navigate to Recent Payments and then click
            'View all' to see your payment history. <br />
            <br />
            2. Alternatively, click on the 'Payments' page from the left-side
            menu, then select the 'Payments' tab to view your payment records.
          </>
        ),
      },
      {
        id: 4,
        question: "How do I view my payment receipts?",
        answer: (
          <>
            Accessing your receipts depends on the type of payment you've made.
            Remember, the receipts are only available for processed payments.{" "}
            <br />
            <br />
            <b>For one-time single payments:</b> Go to the 'Payments' page,
            under the 'Payments' tab. Find the specific payment using filters or
            a date range. Click the three dots to see options to preview or
            download the receipt.
            <br />
            <br /> <b>For scheduled payments:</b> Navigate to the 'Payments'
            page, 'Scheduled' tab. Locate your payment using filters or a date
            range. Click the three dots to see options for previewing or
            downloading the invoice/receipt. <br />
            <br />
            <b>For payments under a payment agreement:</b> Visit the
            'Subscriptions & Plans' page. Use filters and/or a date range to
            find the relevant plan. Click on the three dots next to the plan and
            select 'Preview'. At the bottom, you'll find 'Invoice History'.
            Click the Export icon to open and preview the invoice in a new tab,
            where you can also download it. <br />
            <br />
            Additionally, you will receive a copy of your receipt via email for
            every processed payment.
          </>
        ),
      },
      {
        id: 5,
        question:
          "What should I do if I encounter an error during the payment process?",
        answer: (
          <>
            First, try re-entering your payment information to ensure it's
            accurate. If the issue persists, consider switching to a different
            payment method, if available. Should the error continue, please
            don't hesitate to{" "}
            <a
              className="text-blue hover:!text-btnPrimaryHover underline cursor-pointer"
              href={`mailto:${companyDetails?.email || ""}`}
            >
              contact us
            </a>{" "}
            for further assistance.
          </>
        ),
      },
      {
        id: 6,
        question: "Can I change or cancel a payment once it has been made?",
        answer: (
          <>
            Unfortunately, you cannot change or cancel a payment directly from
            the customer portal once it has been made. For any modifications or
            cancelations, please{" "}
            <a
              className="text-blue hover:!text-btnPrimaryHover underline cursor-pointer"
              href={`mailto:${companyDetails?.email || ""}`}
            >
              contact us
            </a>
            . Alternatively, you can submit a cancellation or change request on
            this support page.
          </>
        ),
      },
      {
        id: 7,
        question:
          "Are there any fees associated with using the payment portal?",
        answer: (
          <>
            Using the customer portal itself is free of charge. However,
            depending on your chosen payment method, you may incur a convenience
            fee.
          </>
        ),
      },
      {
        id: 8,
        question:
          "How do I update my billing information or payment method on file?",
        answer: (
          <>
            To update your existing payment method, simply navigate to the
            'Payment Methods' page in the portal, where you can manage and
            update your payment information as needed.
          </>
        ),
      },
      {
        id: 9,
        question: "Who can I contact for support with the payment portal?",
        answer: (
          <>
            For any assistance with the payment portal, feel free to{" "}
            <a
              className="text-blue hover:!text-btnPrimaryHover underline cursor-pointer"
              href={`mailto:${companyDetails?.email || ""}`}
            >
              contact us
            </a>
            . Additionally, you may find helpful information in our other FAQs
            that could address your query.
          </>
        ),
      },
      {
        id: 10,
        question: "What payment methods does the portal support?",
        answer: (
          <>
            We accept a variety of payment methods for your convenience. These
            include bank payments, card transactions, wire transfers, PayPal,
            Cash App, and After Pay. However, please be aware that the
            availability of these options may vary depending on your account. To
            see the specific methods accessible to you, simply click on 'Make
            Payment' in the portal.
          </>
        ),
      },
      {
        id: 11,
        question: "What happens if I make a duplicate payment by mistake?",
        answer: (
          <>
            If you accidentally make a duplicate payment, please{" "}
            <a
              className="text-blue hover:!text-btnPrimaryHover underline cursor-pointer"
              href={`mailto:${companyDetails?.email || ""}`}
            >
              contact us
            </a>{" "}
            as soon as possible to initiate a refund. Alternatively, you can
            submit a cancellation and refund request on this support page.
          </>
        ),
      },
      {
        id: 12,
        question: "What should I do if my scheduled payment fails?",
        answer: (
          <>
            In the event a scheduled payment fails, you will see a 'Pay' button
            next to the failed transaction everywhere within the portal. Click
            'Pay', and you'll be prompted to update your payment method and
            retry.
            <br />
            <br />
            Additionally, you'll receive an email notification about the failed
            transaction with a link to update your payment.
          </>
        ),
      },
      {
        id: 13,
        question:
          "What is the difference between a custom plan and a payment agreement?",
        answer: (
          <>
            A custom plan involves one or a series of payment transactions
            scheduled for future dates. In this case, a{" "}
            {companyDetails?.name || companyDetails?.company_name}{" "}
            representative creates a schedule on your behalf, with the first
            transaction processing on the designated start date. <br />
            <br />
            On the other hand, a payment agreement also consists of one or more
            scheduled payment transactions for the future. However, in this
            scenario, a company representative sends you an agreement outlining
            the terms between you and the company. This agreement must be signed
            by you to become active. If you do not sign the document, the
            scheduled charges will not be processed. You can view and access the
            signed agreement under the 'Documents' page, which is located in the
            left-side menu of the portal.
          </>
        ),
      },
      {
        id: 14,
        question: "How do I change my profile information?",
        answer: (
          <>
            To update your profile information, click on your profile picture in
            the bottom left-side corner of the portal. Then select 'My Profile'.
            On this page, you have the option to modify your first and last
            name, email address, phone number, and address. Simply hover over
            any of these fields, click on 'Edit', make your changes, and then
            hit 'Save'.
          </>
        ),
      },
      {
        id: 15,
        question: "How long does the refund process take?",
        answer: (
          <>
            The refund typically takes between 5-10 business days to be
            reflected in your bank account.
          </>
        ),
      },
    ],
    [companyDetails]
  );

  const onGetLeadData = useCallback(async () => {
    const lead_payload: any = {
      accounts_id: userData.data.accountData.id,
      cluster_id: userData.data.accountData.clusterid,
      accountid: userData.data.accountData.accountid,
    };
    try {
      await dispatch(leadData(lead_payload)).unwrap();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log("onGetLeadData error", error);
    }
  }, [dispatch, userData]);

  useEffect(() => {
    if (
      isLogin &&
      accountLeadData &&
      Object.keys(accountLeadData).length === 0
    ) {
      onGetLeadData();
    }
  }, [isLogin, accountLeadData, onGetLeadData]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormState((prevState) => ({ ...prevState, [name]: value }));
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    },
    []
  );

  const validateForm = () => {
    let valid = true;
    let newErrors = { name: "", email: "", message: "" };

    if (!formState.name.trim()) {
      newErrors.name = "Name is required.";
      valid = false;
    } else if (!/^[\p{L}\p{M}\s.'’-]+$/u.test(formState.name.trim())) {
      newErrors.name = "Name is invalid.";
      valid = false;
    }
    if (!formState.email.trim()) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      newErrors.email = "Email is invalid.";
      valid = false;
    }
    if (!formState.message.trim()) {
      newErrors.message = "Message is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!validateForm()) return;
      setLoading(true);
      const payment_payload = {
        lead_id: accountLeadData?.lead_id,
        clusterid: userData.data.accountData.clusterid,
        formState,
      };
      try {
        const response: any = await dispatch(supportPost(payment_payload));
        if (response?.payload?.body?.success) {
          setSuccessMessage(true);
        }
      } catch (error) {
        console.log("handleSubmit error ======", error);
      } finally {
        setLoading(false);
        setFormState({ name: "", email: "", message: "" });
      }
    },
    [dispatch, accountLeadData, userData, formState]
  );

  return (
    <>
      <div
        className="lg:pt-[93px] h-full lg:overflow-y-scroll"
        {...sectionAttrs}
      >
        <div className="lg:p-10 p-5 flex xl:flex-row flex-col justify-between lg:gap-5 gap-[30px]">
          <div
            className="flex xl:hidden lg:flex-row lg:flex-wrap flex-col gap-8 w-full"
            {...mobileCardsAttrs}
          >
            {supportOptions.map(
              ({
                id,
                Icon,
                title,
                description,
                onClick,
                className,
                testId,
              }) => (
                <div key={id} className={`max-w-[374px] w-full mx-auto`}>
                  <SupportCard
                    Icon={Icon}
                    title={title}
                    description={description}
                    onClick={onClick}
                    className={className}
                    testId={tid(`${testId}-mobile`)}
                  />
                </div>
              )
            )}
          </div>
          <div className="w-full" {...faqAttrs}>
            <h1
              className="lg:text-2xl text-xl text-bold"
              data-testid={tid("faq-title")}
            >
              FAQ
            </h1>
            {faqQuestions.map(({ id, question, answer }) => (
              <FAQItem
                key={id}
                id={id}
                question={question}
                answer={answer}
                isLoading={isCompanyLoading}
                isOpen={openFaqId === id}
                onToggle={() =>
                  setOpenFaqId((prev) => (prev === id ? null : id))
                }
              />
            ))}
          </div>
          <div
            className="xl:flex hidden flex-col gap-8 max-w-[374px] w-full"
            {...desktopCardsAttrs}
          >
            {supportOptions.map(
              ({
                id,
                Icon,
                title,
                description,
                onClick,
                className,
                testId,
              }) => (
                <SupportCard
                  key={id}
                  Icon={Icon}
                  title={title}
                  description={description}
                  onClick={onClick}
                  className={className}
                  testId={tid(`${testId}-desktop`)}
                />
              )
            )}
          </div>
        </div>
      </div>

      <CustomModal
        otpModal
        isOpen={isOpenRequest}
        title={`${successMessage ? "" : "Get In Touch"}`}
        doneText="Submit"
        className="w-[680px] rounded-[10px]"
        cancleText="Not Now"
        onCloseClick={() => {
          setIsOpenRequest(false);
          setSuccessMessage(false);
          setFormState({
            name: "",
            email: "",
            message: "",
          });
        }}
        children={
          successMessage ? (
            <div className="text-center pt-7 px-10 " {...successAttrs}>
              <SuccessIcon className="mx-auto" aria-hidden="true" />
              <p className="text-txtPrimary text-xl font-bold mt-8">Success</p>
              <p className="mt-8 text-txtParagraph">
                Thank you for reaching out!
              </p>
              <p className="mt-4 text-txtParagraph">
                Your message has been successfully sent. We'll get back to{" "}
                <br /> you as soon as possible.
              </p>
              {isCompanyLoading ? (
                <p className="mt-4">
                  <Skeleton count={2} />
                </p>
              ) : (
              <p className="mt-4 text-txtParagraph">
                Alternatively, you can also contact us via email at{" "}
                <a
                  className="text-blue hover:!text-btnPrimaryHover"
                  href={`mailto:${companyDetails?.email || ""}`}
                  data-testid={tid("request-success-email-link")}
                >
                  {companyDetails?.email}
                </a>{" "}
                or give us a call at{" "}
                <span className="text-blue hover:!text-btnPrimaryHover">
                  {formatNumber(
                    companyDetails.phone_number,
                    companyDetails.countryCode || "1",
                    true
                  ) || "+1 (404) 410 4100"}
                </span>
                .
              </p>
              )}
              <Button
                text="Done"
                className="w-full mt-7"
                data-testid={tid("request-success-done-button")}
                aria-label="Close support request success message"
                onClick={() => {
                  setIsOpenRequest(false);
                  setSuccessMessage(false);
                }}
              />
            </div>
          ) : (
            <form onSubmit={handleSubmit} {...requestFormAttrs}>
              <div className="mt-7">
                <InputField
                  showLabel
                  name="name"
                  label="Name"
                  placeholder="Enter your name"
                  value={formState.name}
                  onChange={handleInputChange}
                  className="!py-2"
                  _className="text-xs "
                  data-testid={tid("request-name-input")}
                  aria-label="Support request name"
                />
                {errors.name && (
                  <p className="text-red text-xs mt-1">{errors.name}</p>
                )}
              </div>
              <div className="mt-7">
                <InputField
                  showLabel
                  name="email"
                  label="Email address"
                  placeholder="Enter your email address"
                  value={formState.email}
                  onChange={handleInputChange}
                  className="!py-2"
                  _className="text-xs "
                  data-testid={tid("request-email-input")}
                  aria-label="Support request email address"
                />
                {errors.email && (
                  <p className="text-red text-xs mt-1">{errors.email}</p>
                )}
              </div>
              <div className="mt-7">
                <InputField
                  showLabel
                  name="message"
                  label="Message"
                  textarea
                  placeholder="Type your message here..."
                  value={formState.message}
                  onChange={handleInputChange}
                  className=""
                  _className="text-xs "
                  data-testid={tid("request-message-input")}
                  aria-label="Support request message"
                />
                {errors.message && (
                  <p className="text-red text-xs ">{errors.message}</p>
                )}
              </div>
              <Button
                text="Submit"
                className="w-full mt-5"
                type="submit"
                isDisabled={
                  formState.email.trim() === "" ||
                  formState.message.trim() === "" ||
                  formState.name.trim() === ""
                }
                isLoading={loading}
                data-testid={tid("request-submit-button")}
                aria-label="Submit support request"
              />
            </form>
          )
        }
      />
    </>
  );
};

export default SupportPage;
